import { filter } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Currency } from '@/common/models'
import { Header } from '@/components'
import { getCurrencies } from '@/core/services/festival-settings'
import {
  type CountriesStats,
  type PaymentMethodsStats,
  type SalesStats,
  type Stats,
  type TicketsStats,
  getCountriesStats,
  getPaymentMethodsStats,
  getSlatesStats,
  getTicketsStats,
  getVouchersStats,
} from '../../../core/services/stats'
import { useAppSelector } from '../../../store'
import { DiscountCodesWidget } from './DiscountCodesWidget'
import { PaymentMethodsWidget } from './PaymentMethodsWidget'
import { PeriodDropdown } from './PeriodDropdown'
import { SalesGraphicWidget } from './SalesGraphicWidget'
import { TicketTypesWidget } from './TicketTypesWidget'
import { TopCountriesWidget } from './TopCountriesWidget'
import Loader from '@/common/components/Loader'

const formatDateLabel = (
  timestamp: number,
  granularity: 'MONTH' | 'DAY' | 'HOUR',
) => {
  const date = new Date(timestamp)
  switch (granularity) {
    case 'MONTH':
      return date.toLocaleString('en', { month: 'short' })
    case 'DAY':
      return `${date.getDate()} ${date.toLocaleString('en', {
        month: 'short',
      })}`
    case 'HOUR':
      return `${date.getHours().toString().padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
  }
}

const getTimeRange = (period: string) => {
  const now = Date.now()
  const durations: Record<string, number> = {
    today: 24 * 60 * 60 * 1000,
    '1w': 7 * 24 * 60 * 60 * 1000,
    '1m': 30 * 24 * 60 * 60 * 1000,
    '3m': 90 * 24 * 60 * 60 * 1000,
    '6m': 180 * 24 * 60 * 60 * 1000,
  }
  if (period === 'all') {
    return { startTime: 0, endTime: now }
  }
  return {
    startTime: now - (durations[period] ?? durations['6m']),
    endTime: now,
  }
}

const PERIOD_LABELS: Record<string, string> = {
  all: 'All Time',
  '6m': 'Last 6 months',
  '3m': 'Last 3 months',
  '1m': 'Last 1 month',
  '1w': 'Last Week',
  today: 'Today',
}

export const DashboardPage: React.FC = () => {
  const { user, festivals } = useAppSelector((s) => s.app)

  const { festivalName, editionId } = useParams()

  const navigate = useNavigate()

  const filterFestivals = useMemo(() => {
    return filter(festivals, (one) => {
      if (user?.superAdmin) {
        return true
      }
      if (
        user?.festivalsPermissions.some(
          (o) => o.festivalName === one.platformName,
        )
      ) {
        return true
      }
      return false
    })
  }, [user?.festivalsPermissions, festivals])

  useEffect(() => {
    if (
      user?.festivalsPermissions.length === 1 &&
      user?.festivalsPermissions[0]?.permissions?.filter((p) => p.view)
        .length === 1 &&
      user?.festivalsPermissions[0]?.permissions?.filter((p) => p.view)[0]
        .type === 'SUPPORT' &&
      festivals.length === 1
    ) {
      const redirectTo = `/support/${filterFestivals[0].platformName}/${
        filterFestivals[0].editions.filter((e) => e.active)[0].id
      }`
      navigate(redirectTo, { replace: true })
      return
    }

    if (!festivalName || !editionId) {
      const firstFestival = filterFestivals[0]
      if (firstFestival) {
        const activeEdition = firstFestival.editions.find((e) => e.active)
        const edition = activeEdition ?? firstFestival.editions[0]
        if (edition) {
          navigate(`/dashboard/${firstFestival.platformName}/${edition.id}`, {
            replace: true,
          })
        }
      }
    }
  }, [festivalName, editionId, filterFestivals, navigate, user, festivals])

  const [period, setPeriod] = useState('1m')
  const [salesData, setSalesData] = useState<SalesStats | null>(null)
  const [vouchersData, setVouchersData] = useState<Stats | null>(null)
  const [ticketsData, setTicketsData] = useState<TicketsStats | null>(null)
  const [paymentData, setPaymentData] = useState<PaymentMethodsStats | null>(
    null,
  )
  const [countriesData, setCountriesData] = useState<CountriesStats | null>(
    null,
  )

  const [currency, setCurrency] = useState<Currency | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const getCurrency = useCallback(async () => {
    try {
      const response = await getCurrencies()
      if (response.status === 200) {
        setCurrency(response.data.defaultCurrency)
      }
    } catch (e) {
      console.error(e)
    }
  }, [])

  useEffect(() => {
    getCurrency()
  }, [getCurrency, editionId, festivalName])

  const fetchStats = useCallback(async () => {
    const { startTime, endTime } = getTimeRange(period)
    const params = {
      startTime,
      endTime,
      festivalEditionId: editionId as string,
    }

    setLoading(true)

    const [sales, vouchers, tickets, payments, countries] =
      await Promise.allSettled([
        getSlatesStats(params),
        getVouchersStats(params),
        getTicketsStats(params),
        getPaymentMethodsStats(params),
        getCountriesStats(params),
      ])

    if (sales.status === 'fulfilled') setSalesData(sales.value.data)
    if (vouchers.status === 'fulfilled') setVouchersData(vouchers.value.data)
    if (tickets.status === 'fulfilled') setTicketsData(tickets.value.data)
    if (payments.status === 'fulfilled') setPaymentData(payments.value.data)
    if (countries.status === 'fulfilled') setCountriesData(countries.value.data)

    setLoading(false)
  }, [period, editionId])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const salesChartData = useMemo(() => {
    if (!salesData?.items?.length) return undefined
    return salesData.items.map((item) => ({
      label: formatDateLabel(item.date, salesData.granularity),
      revenue: item.amount,
    }))
  }, [salesData])

  const totalSales = useMemo(() => {
    if (!salesData?.items?.length) return undefined
    const total = salesData.items.reduce((sum, item) => sum + item.amount, 0)
    const symbol = currency?.symbol ?? '€'
    return `${symbol}${total.toLocaleString()}`
  }, [salesData, currency])

  const paymentMethods = useMemo(() => {
    if (!paymentData) return undefined
    const total = paymentData.fiat + paymentData.crypto
    if (total === 0) return undefined
    return [
      {
        label: 'Crypto',
        percentage: Math.round((paymentData.crypto / total) * 100),
        color: '#06FF48',
      },
      {
        label: 'Fiat',
        percentage: Math.round((paymentData.fiat / total) * 100),
        color: '#FFD700',
      },
    ]
  }, [paymentData])

  const ticketTypes = useMemo(() => {
    if (!ticketsData?.items?.length) return undefined
    return ticketsData.items.map((item) => ({
      name: item.ticket,
      description: item.category,
      count: item.count,
    }))
  }, [ticketsData])

  const discountCodes = useMemo(() => {
    if (!vouchersData?.vouchers?.length) return undefined
    return vouchersData.vouchers.map((v) => ({
      code: v.voucherCode,
      count: v.count,
    }))
  }, [vouchersData])

  const topCountries = useMemo(() => {
    if (!countriesData?.items?.length) return undefined
    return countriesData.items.map((item) => ({
      name: item.country,
      count: item.count,
    }))
  }, [countriesData])

  const subtitle = PERIOD_LABELS[period] ?? 'Last 6 Months'

  if (!festivalName || !editionId) return null

  return (
    <>
      <Header pageTitle={`Dashboard - ${festivalName}`} />
      {loading && <Loader />}
      <div className="flex flex-col gap-4 max-h-[calc(100vh-113px)] overflow-y-auto">
        {/* Dashboard Header */}
        <div className="flex items-center justify-center">
          <PeriodDropdown value={period} onChange={setPeriod} />
        </div>
        {/* Widgets Grid */}
        <div className="grid grid-cols-4 gap-4">
          <SalesGraphicWidget
            className="col-span-2 min-h-[432px]"
            data={salesChartData}
            soldTicketsCount={salesData?.soldTicketsCount}
            totalSales={totalSales}
            subtitle={subtitle}
            currencySymbol={currency?.symbol}
          />
          <PaymentMethodsWidget
            className="col-span-1 min-h-[432px]"
            methods={paymentMethods}
            soldTicketsCount={salesData?.soldTicketsCount}
            totalSales={totalSales}
            subtitle={subtitle}
          />
          <TicketTypesWidget
            className="col-span-1 min-h-[432px]"
            tickets={ticketTypes}
            subtitle={subtitle}
            onArrowClick={() => {
              navigate(`/tickets/categories/${festivalName}/${editionId}`)
            }}
          />
          <DiscountCodesWidget
            className="col-span-2 min-h-[432px]"
            codes={discountCodes}
            subtitle={subtitle}
          />
          <TopCountriesWidget
            className="col-span-2 min-h-[432px]"
            countries={topCountries}
            subtitle={subtitle}
          />
        </div>
      </div>
    </>
  )
}
